import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import AnnouncementBanner from '../../components/AnnouncementBanner';
import FloatingWhatsApp from '../../components/FloatingWhatsApp';
import MobileBottomNav from '../../components/MobileBottomNav';
import SEOSchema from '../../components/SEOSchema';
import {
  Scale, Droplets, Flame, Beef, Activity, PieChart, Ruler,
  ArrowRight, Calculator, Target, Dumbbell, CircleDot, Heart,
  Wind, Timer, Baby, Moon, Clock, Zap, Footprints, Gauge, Stethoscope
} from 'lucide-react';

const categories = [
  {
    name: 'Body Composition',
    accent: 'text-blue-300',
    calculators: [
      { slug: 'bmi', name: 'BMI Calculator', desc: 'Body Mass Index + healthy weight range.', icon: Scale, accent: 'from-blue-500 to-cyan-500' },
      { slug: 'bmi-prime', name: 'BMI Prime', desc: 'Ratio variation of BMI for clearer classification.', icon: Gauge, accent: 'from-blue-500 to-violet-500' },
      { slug: 'body-fat', name: 'Body Fat %', desc: 'U.S. Navy body fat % and lean body mass.', icon: Ruler, accent: 'from-violet-500 to-fuchsia-500' },
      { slug: 'lean-body-mass', name: 'Lean Body Mass', desc: 'Body weight excluding fat.', icon: Activity, accent: 'from-emerald-500 to-cyan-500' },
      { slug: 'ideal-weight', name: 'Ideal Weight', desc: 'Devine, Hamwi, Robinson, Miller formulas.', icon: Target, accent: 'from-emerald-500 to-blue-500' },
      { slug: 'ffmi', name: 'FFMI', desc: 'Fat-Free Mass Index for advanced muscle analysis.', icon: Dumbbell, accent: 'from-orange-500 to-rose-500' },
      { slug: 'waist-to-hip', name: 'Waist-to-Hip Ratio', desc: 'Fat distribution & metabolic risk.', icon: CircleDot, accent: 'from-rose-500 to-orange-500' },
      { slug: 'bsa', name: 'Body Surface Area', desc: 'Mosteller formula, a medical reference.', icon: Stethoscope, accent: 'from-sky-500 to-teal-500' }
    ]
  },
  {
    name: 'Nutrition & Energy',
    accent: 'text-emerald-300',
    calculators: [
      { slug: 'tdee', name: 'TDEE Calculator', desc: 'Total Daily Energy Expenditure.', icon: Activity, accent: 'from-emerald-500 to-green-500' },
      { slug: 'calorie-deficit', name: 'Calorie Deficit', desc: 'Science-backed deficit for fat loss.', icon: Flame, accent: 'from-orange-500 to-amber-500' },
      { slug: 'macro', name: 'Macro Calculator', desc: 'Protein, carbs and fats, gram by gram.', icon: PieChart, accent: 'from-amber-500 to-rose-500' },
      { slug: 'protein-intake', name: 'Protein Intake', desc: 'Daily protein target for your goal.', icon: Beef, accent: 'from-rose-500 to-pink-500' },
      { slug: 'water-intake', name: 'Water Intake', desc: 'Daily hydration target.', icon: Droplets, accent: 'from-cyan-500 to-blue-500' },
      { slug: 'intermittent-fasting', name: 'Intermittent Fasting', desc: '16:8, 18:6, OMAD windows.', icon: Clock, accent: 'from-teal-500 to-emerald-500' }
    ]
  },
  {
    name: 'Fitness & Performance',
    accent: 'text-orange-300',
    calculators: [
      { slug: 'one-rep-max', name: 'One Rep Max', desc: 'Strength estimate with training % chart.', icon: Dumbbell, accent: 'from-amber-500 to-orange-500' },
      { slug: 'heart-rate-zones', name: 'Heart Rate Zones', desc: 'Training zones for cardio & HIIT.', icon: Heart, accent: 'from-rose-500 to-pink-500' },
      { slug: 'vo2-max', name: 'VO₂ Max', desc: 'Estimate your aerobic capacity.', icon: Wind, accent: 'from-cyan-500 to-blue-500' },
      { slug: 'pace', name: 'Pace Calculator', desc: 'Pace, speed & race time predictions.', icon: Timer, accent: 'from-emerald-500 to-cyan-500' },
      { slug: 'calorie-burn', name: 'Calorie Burn', desc: 'Calories burned for 15+ activities.', icon: Zap, accent: 'from-amber-500 to-orange-500' },
      { slug: 'steps-to-calories', name: 'Steps to Calories', desc: 'Convert steps to distance & calories.', icon: Footprints, accent: 'from-lime-500 to-emerald-500' }
    ]
  },
  {
    name: 'Women & Recovery',
    accent: 'text-pink-300',
    calculators: [
      { slug: 'pregnancy-weight-gain', name: 'Pregnancy Weight Gain', desc: 'IOM guidelines by trimester.', icon: Baby, accent: 'from-pink-500 to-rose-500' },
      { slug: 'sleep', name: 'Sleep Calculator', desc: 'Optimal sleep cycles for recovery.', icon: Moon, accent: 'from-indigo-500 to-violet-500' }
    ]
  }
];

const CalculatorsIndexPage = () => {
  const totalCalculators = categories.reduce((acc, cat) => acc + cat.calculators.length, 0);

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>{`${totalCalculators}+ Free Fitness & Nutrition Calculators | TCUBE FITNESS™`}</title>
        <meta name="description" content={`Free, doctor-led fitness and nutrition calculators, ${totalCalculators}+ tools including BMI, TDEE, macros, body fat, VO₂ max, 1RM, heart rate zones and more.`} />
        <link rel="canonical" href="https://tcubefitness.com/calculators" />
      </Helmet>

      <SEOSchema
        type="webpage"
        name="TCUBE FITNESS™ Health & Fitness Calculators"
        description={`${totalCalculators}+ free, evidence-based health and fitness calculators covering BMI, body fat, BMR, calories, heart rate, hydration, pregnancy, and more.`}
        url="/calculators"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Calculators', path: '/calculators' },
        ]}
      />

      <AnnouncementBanner />
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-32 sm:pt-36 pb-24 lg:pb-16">
        <div className="container mx-auto px-4 sm:px-6">

          {/* Hero */}
          <section className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-5">
              <Calculator className="w-4 h-4" aria-hidden="true" />
              {totalCalculators} Calculators
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              Free, Doctor-Guided{' '}
              <span className="bg-gradient-to-r from-blue-400 via-orange-400 to-emerald-400 bg-clip-text text-transparent">
                Fitness Calculators
              </span>
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              {totalCalculators} science-backed calculators across body composition, nutrition, performance and recovery, all metric &amp; imperial.
            </p>
          </section>

          {/* Categories */}
          {categories.map((cat) => (
            <section key={cat.name} className="mb-12 sm:mb-16" data-testid={`calc-category-${cat.name.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              <div className="flex items-baseline justify-between mb-5 max-w-6xl mx-auto">
                <h2 className={`text-xl sm:text-2xl font-bold ${cat.accent}`}>{cat.name}</h2>
                <span className="text-slate-500 text-xs sm:text-sm">{cat.calculators.length} tools</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-w-6xl mx-auto">
                {cat.calculators.map((c) => {
                  const Icon = c.icon;
                  return (
                    <Link
                      key={c.slug}
                      to={`/calculators/${c.slug}`}
                      className="group bg-slate-900/60 hover:bg-slate-900/90 border border-slate-800 hover:border-slate-700 rounded-2xl p-5 transition-all relative overflow-hidden"
                      data-testid={`calc-card-${c.slug}`}
                    >
                      <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl bg-gradient-to-br ${c.accent} opacity-10 group-hover:opacity-20 transition-opacity`} aria-hidden="true"></div>
                      <div className={`relative w-10 h-10 rounded-xl bg-gradient-to-br ${c.accent} bg-opacity-20 flex items-center justify-center mb-3 border border-white/10`}>
                        <Icon className="w-5 h-5 text-white" aria-hidden="true" />
                      </div>
                      <h3 className="relative text-base sm:text-lg font-bold text-white mb-1">{c.name}</h3>
                      <p className="relative text-slate-400 text-xs sm:text-sm leading-relaxed">{c.desc}</p>
                      <span className="relative inline-flex items-center gap-1 mt-3 text-xs font-semibold text-slate-300 group-hover:text-white">
                        Open <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}

          {/* CTA */}
          <section className="max-w-4xl mx-auto bg-gradient-to-br from-blue-900/40 to-emerald-900/30 border border-blue-500/30 rounded-3xl p-8 sm:p-10 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Calculators are a starting point.</h2>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto text-sm sm:text-base">
              A doctor-guided plan considers your medical history, lifestyle, and goals, so book a free consultation to get yours.
            </p>
            <Link
              to="/book-consultation?from=calculators-index"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white px-7 py-3.5 rounded-full font-bold text-sm sm:text-base shadow-lg shadow-blue-500/25 transition-all"
              data-testid="calculators-cta-book"
            >
              Book Free Consultation
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </section>
        </div>
      </main>

      <Footer />
      <FloatingWhatsApp />
      <MobileBottomNav />
    </div>
  );
};

export default CalculatorsIndexPage;
